import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "./Tables.css";
import { secureStorage } from "./utils/secureStorage";
import { jwtDecode } from "jwt-decode";
import * as XLSX from "xlsx";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const DEFAULT_COLUMN_ROWS = 6;

const Tables = () => {
  const fileInputRef = useRef(null);
  const token = secureStorage.getItem("token");

  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [columns, setColumns] = useState([]);
  const [records, setRecords] = useState([]);
  const [isLoadingTables, setIsLoadingTables] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState(1);
  const [tableName, setTableName] = useState("");
  const [initialRows, setInitialRows] = useState([]);

  const [isRenaming, setIsRenaming] = useState(false);
  const [tableNameDraft, setTableNameDraft] = useState("");
  const [tableToDelete, setTableToDelete] = useState(null); // store table info for modal
  const [activeTableChat, setActiveTableChat] = useState(null); 
  // { table, messages: [] }

  const [tableChatInput, setTableChatInput] = useState("");
  const [isAskingTable, setIsAskingTable] = useState(false);

  const [columnsInput, setColumnsInput] = useState(
    Array.from({ length: DEFAULT_COLUMN_ROWS }).map(() => ({
      name: "",
      type: "text",
    }))
  );

  const [showAddRecordModal, setShowAddRecordModal] = useState(false);
  const [newRecord, setNewRecord] = useState({});

  const chatContainerRef = useRef(null);

  // After updating messages
  useEffect(() => {
  if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }
  }, [activeTableChat?.messages, isAskingTable]);


  // Get org from JWT
  const getOrgId = () => {
    const t = secureStorage.getItem("token");
    if (!t) return null;
    try {
      const decoded = jwtDecode(t);
      return decoded.org_id;
    } catch {
      return null;
    }
  };

  const [organizationId, setOrganizationId] = useState(null);

  useEffect(() => {
    const oid = getOrgId();
    if (oid) setOrganizationId(oid);
  }, []);

  useEffect(() => {
    if (organizationId) fetchTables();
  }, [organizationId]);

  // Get all tables (columns + records are included)
  const fetchTables = async () => {
    setIsLoadingTables(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tables/${organizationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error();

      const data = await res.json();
      setTables(data);
    } catch (e) {
      toast.error("Unable to load tables.");
    } finally {
      setIsLoadingTables(false);
    }
  };

  // Load selected table: use already fetched schema
  const loadTable = (table) => {
    setSelectedTable(table);
    setColumns(table.columns || []);
    setRecords(table.records || []);
  };

  // Add record
  const handleAddRecord = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/tables/record/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          table_id: selectedTable.id,
          data: newRecord,
        }),
      });

      if (!res.ok) throw new Error();

      const created = await res.json();
      setRecords([...records, created]);

      toast.success("Record added successfully");
      setShowAddRecordModal(false);
      setNewRecord({});
    } catch {
      toast.error("Failed to add record");
    }
  };

  const handleDeleteRecord = async (recordId) => {
    // if (!window.confirm("Delete this row?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/tables/record/delete`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ record_id: recordId }),
      });
  
      if (!res.ok) throw new Error();
  
      // Update UI
      setRecords((prev) => prev.filter((r) => r.id !== recordId));
      toast.success("Record deleted");
    } catch {
      toast.error("Failed to delete record");
    }
  };  

  // Edit record
  const handleEditField = async (recordId, field, value) => {
    try {
      const res = await fetch(`${API_BASE_URL}/tables/record/edit`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          record_id: recordId,
          field,
          value,
        }),
      });

      if (!res.ok) throw new Error();

      const updatedRecord = await res.json();

      setRecords((prev) =>
        prev.map((r) => (r.id === recordId ? updatedRecord : r))
      );
    } catch {
      toast.error("Failed to update");
    }
  };

  // Create table
  const handleFinalCreateTable = async () => {
    const finalColumns = columnsInput
      .filter((col) => col.name.trim() !== "")
      .map((c) => ({
        name: c.name,
        type: c.type || "text",
      }));

    if (!tableName.trim()) return toast.error("Enter a table name");
    if (finalColumns.length === 0) return toast.error("Add at least 1 column");

    try {
      const res = await fetch(`${API_BASE_URL}/tables/create/full`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          organization_id: organizationId,
          name: tableName,
          columns: finalColumns,
          initial_rows: initialRows,
        }),
      });

      if (!res.ok) throw new Error();

      toast.success("Table created successfully");
      setShowCreateModal(false);
      setTableName("");
      setInitialRows([]);
      setColumnsInput(
        Array.from({ length: DEFAULT_COLUMN_ROWS }).map(() => ({
          name: "",
          type: "text",
        }))
      );

      fetchTables();
    } catch {
      toast.error("Failed to create table");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    if (!organizationId) {
      toast.error("Organization not found");
      return;
    }
  
    const formData = new FormData();
    formData.append("file", file);
  
    try {
      const res = await fetch(
        `${API_BASE_URL}/tables/upload?organization_id=${organizationId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`, // ❗ do NOT set Content-Type
          },
          body: formData,
        }
      );
  
      if (!res.ok) throw new Error();
  
      const createdTable = await res.json();
  
      // Add new table to list
      setTables((prev) => [...prev, createdTable]);
  
      // Auto-open the uploaded table
      loadTable(createdTable);
  
      toast.success(`Table "${createdTable.name}" uploaded`);
    } catch (err) {
      toast.error("Failed to upload file");
    } finally {
      // Reset file input so same file can be uploaded again
      e.target.value = "";
    }
  };

  // Function to save table name
  const saveTableName = async () => {
    if (!tableNameDraft.trim() || tableNameDraft === selectedTable.name) {
      setIsRenaming(false);
      return;
    }
  
    try {
      const res = await fetch(`${API_BASE_URL}/tables/rename`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ table_id: selectedTable.id, name: tableNameDraft }),
      });
  
      if (!res.ok) throw new Error();
  
      const updated = await res.json();
      setSelectedTable((prev) => ({ ...prev, name: updated.name }));
      setTables((prev) =>
        prev.map((t) => (t.id === selectedTable.id ? { ...t, name: updated.name } : t))
      );
      toast.success("Table renamed");
    } catch {
      toast.error("Failed to rename table");
      setTableNameDraft(selectedTable.name);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDeleteTable = async (tableId) => {
    // if (!window.confirm("Are you sure you want to delete this table?")) return;
  
    try {
      const res = await fetch(`${API_BASE_URL}/tables/${tableId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
  
      setTables((prev) => prev.filter((t) => t.id !== tableId));
      if (selectedTable?.id === tableId) setSelectedTable(null);
      setTableToDelete(null); // close modal
      toast.success("Table deleted successfully");
    } catch {
      toast.error("Failed to delete table");
    }
  };

  const askTable = async (tableId, question) => {
    if (!question.trim()) return;
  
    setIsAskingTable(true);
  
    // Immediately add user message
    setActiveTableChat(prev => ({
      ...prev,
      messages: [
        ...prev.messages,
        { role: "user", content: question }
      ]
    }));
  
    try {
      const res = await fetch(`${API_BASE_URL}/tables/ask-table/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ table_id: tableId, question }),
      });
  
      const data = await res.json();
  
      // Append assistant response only
      if (data.answer) {
        setActiveTableChat(prev => ({
          ...prev,
          messages: [
            ...prev.messages,
            { role: "assistant", content: data.answer }
          ]
        }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsAskingTable(false);
    }
  };
  
  

  return (
    <div className="tables-wrapper">
      <h2 className="tables-title">Tables Workspace</h2>

      <div className="tables-actions">
        <button onClick={() => setShowCreateModal(true)}>Create Table</button>
        <button onClick={() => fileInputRef.current.click()}>
          Upload CSV/XLSX
        </button>
        <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".csv,.xlsx"
            onChange={handleFileUpload}
        />
      </div>

      <div className="tables-list">
        {isLoadingTables ? (
            <p>Loading tables...</p>
        ) : tables.length === 0 ? (
            <p>No tables found.</p>
        ) : (
            tables.map((t) => (
                <div
                    key={t.id}
                    className="table-card"
                    style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px",
                    borderRadius: "8px",
                    background: "#f8f8f8",
                    marginBottom: "8px",
                    }}
                >
                    {/* Table name (still clickable to open table) */}
                    <h3
                    style={{ margin: 0, cursor: "pointer" }}
                    onClick={() => loadTable(t)}
                    >
                    {t.name}
                    </h3>
                
                    {/* Actions */}
                    <div style={{ display: "flex", gap: "8px" }}>
                    {/* Chat button */}
                    <button
                        onClick={(e) => {
                        e.stopPropagation();
                        setActiveTableChat({
                            table: t,
                            sessionId: Date.now(), // or backend-generated
                            messages: [],
                        });
                        }}
                        title="Ask this table"
                        style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "16px",
                        }}
                    >
                        💬
                    </button>
                
                    {/* Delete button */}
                    <button
                        onClick={(e) => {
                        e.stopPropagation();
                        setTableToDelete(t);
                        }}
                        title="Delete Table"
                        style={{
                        color: "red",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: "bold",
                        }}
                    >
                        🗑
                    </button>
                    </div>
                </div>
                ))
            )}
            </div>

            {/* Chat Modal in Tables */}    
            {activeTableChat && (
                <div className="modal-overlay" onClick={() => setActiveTableChat(null)}>
                    <div
                    className="modal-content"
                    onClick={(e) => e.stopPropagation()}
                    style={{ maxWidth: "600px" }}
                    >
                    <h3>Ask {activeTableChat.table.name}</h3>

                    <div className="chat-messages" ref={chatContainerRef}>
                        {activeTableChat?.messages?.map((m, i) => (
                        <div
                            key={i}
                            className={`chat-bubble ${m.role}`}
                        >
                            <strong>{m.role === "user" ? "You" : "Retreeval"}:</strong> {m.content}
                        </div>
                        ))}
                        {isAskingTable && (
                        <div className="chat-bubble assistant typing">
                            Retreeval is typing...
                        </div>
                        )}
                    </div>

                    <div style={{ display: "flex", gap: "8px" }}>
                        <input
                        value={tableChatInput}
                        onChange={(e) => setTableChatInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && tableChatInput.trim() && !isAskingTable) {
                            const question = tableChatInput.trim();
                            setTableChatInput(""); // clear input
                            askTable(activeTableChat.table.id, question);
                            }
                        }}
                        placeholder="Ask a question about this table..."
                        style={{ flex: 1, padding: "8px" }}
                        />
                        <button
                        onClick={() => {
                            if (!tableChatInput.trim() || isAskingTable) return;
                            const question = tableChatInput.trim();
                            setTableChatInput(""); // clear input
                            askTable(activeTableChat.table.id, question);
                        }}
                        disabled={isAskingTable || !tableChatInput.trim()}
                        >
                        {isAskingTable ? "..." : "Send"}
                        </button>
                    </div>
                    </div>
                </div>
                )}

              


      {/* RECORDS SECTION */}
        {selectedTable && (
        <div className="records-section">
            {/* Header: Editable Table Name + Add Row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
            
            {/* Editable Table Name */}
            <h3 style={{ margin: 0 }}>
                {isRenaming ? (
                <input
                    value={tableNameDraft}
                    autoFocus
                    onChange={(e) => setTableNameDraft(e.target.value)}
                    onBlur={saveTableName}
                    onKeyDown={(e) => {
                    if (e.key === "Enter") saveTableName();
                    if (e.key === "Escape") {
                        setIsRenaming(false);
                        setTableNameDraft(selectedTable.name);
                    }
                    }}
                    style={{
                    fontSize: "1.25rem",
                    fontWeight: "600",
                    padding: "4px 6px",
                    }}
                />
                ) : (
                <span
                    onClick={() => {
                    setIsRenaming(true);
                    setTableNameDraft(selectedTable.name);
                    }}
                    style={{ cursor: "pointer" }}
                    title="Click to rename table"
                >
                    {selectedTable.name}
                </span>
                )}
            </h3>

            <button
                onClick={() =>
                setActiveTableChat({
                    table: selectedTable,
                    messages: [],
                })
                }
                title="Ask this table"
                style={{ background: "transparent", border: "none", cursor: "pointer" }}
            >
                💬
            </button>
            


            {/* Add Row Button */}
            <button
                onClick={() => setShowAddRecordModal(true)}
                className="btn-primary"
            >
                + Add Row
            </button>
            </div>

            {/* Records Table */}
            <table className="records-table">
            <thead>
                <tr>
                {columns.map((c) => (
                    <th key={c.name}>{c.name}</th>
                ))}
                <th>Action</th> {/* Delete row */}
                </tr>
            </thead>

            <tbody>
                {records.map((row) => (
                <tr key={row.id}>
                    {columns.map((c) => (
                    <td key={c.name}>
                        <input
                        value={row.data[c.name] || ""}
                        onChange={(e) => {
                            // Update local state instantly
                            setRecords((prev) =>
                            prev.map((r) =>
                                r.id === row.id
                                ? { ...r, data: { ...r.data, [c.name]: e.target.value } }
                                : r
                            )
                            );
                        }}
                        onBlur={(e) => {
                            // Persist change to backend when clicking away
                            handleEditField(row.id, c.name, e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                            handleEditField(row.id, c.name, e.target.value);
                            e.target.blur(); // optional: remove focus
                            }
                        }}
                        />
                    </td>
                    ))}

                    {/* Delete row button */}
                    <td>
                    <button
                        onClick={() => handleDeleteRecord(row.id)}
                        title="Delete Row"
                        style={{
                        background: "transparent",
                        border: "none",
                        color: "red",
                        cursor: "pointer",
                        fontWeight: "bold",
                        }}
                    >
                        ✕
                    </button>
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </div>
        )}


      {/* ADD RECORD MODAL */}
      {showAddRecordModal && (
        <div className="modal-overlay" onClick={() => setShowAddRecordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Record</h3>

            {columns.map((c) => (
              <div key={c.name} className="form-row">
                <label>{c.name}</label>
                <input
                  type="text"
                  value={newRecord[c.name] || ""}
                  onChange={(e) =>
                    setNewRecord({ ...newRecord, [c.name]: e.target.value })
                  }
                />
              </div>
            ))}

            <button onClick={handleAddRecord}>Save</button>
          </div>
        </div>
      )}

        {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Table</h3>

            <label>Table Name</label>
            <input
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
            />

            <h4>Columns</h4>
            {columnsInput.map((c, idx) => (
                <div key={idx} className="form-row">
                <input
                    type="text"
                    placeholder="Column name"
                    value={c.name}
                    onChange={(e) => {
                    const arr = [...columnsInput];
                    arr[idx].name = e.target.value;
                    setColumnsInput(arr);
                    }}
                />
                </div>
            ))}

            <button onClick={handleFinalCreateTable}>Create Table</button>
            </div>
        </div>
        )}

        {tableToDelete && (
        <div
            className="modal-overlay"
            onClick={() => setTableToDelete(null)}
        >
            <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            >
            <h3>Delete Table?</h3>
            <p>
                Are you sure you want to delete "<strong>{tableToDelete.name}</strong>"? This action cannot be undone.
            </p>
            <div className="delete-modal-buttons">
                <button
                onClick={() => setTableToDelete(null)}
                className="btn-secondary"
                >
                Cancel
                </button>
                <button
                onClick={() => handleDeleteTable(tableToDelete.id)}
                className="btn-danger"
                >
                Delete
                </button>
            </div>
            </div>
        </div>
        )}

    </div>
  );
};

export default Tables;


